import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useToasts } from "../../contexts/toasts";
import {
  Alert,
  Button,
  ColorPicker,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Upload,
  UploadFile,
  UploadProps,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { FootballTeam, Pair, PairType, Tournament, TournamentFormat } from "../../types/Tournaments";
import { Option } from "../../models/generics";
import { getMimeType, getSportName } from "../../utils/stringUtil";
import { AdminFootballTeams, Tournaments } from "../../api/Tournaments";
import { Structures } from "../../api/Structure";
import { UserFavorites } from "../../types/Structure";
import FootballRosterEditor from "../../Components/FootballRosterEditor";
import { Sports } from "../../api/Sports";
import Cookies from "js-cookie";
import {
  Card,
  Col,
  Row,
  Button as RBBUtton,
  Container,
  Breadcrumb,
} from "react-bootstrap";
import { TailSpin } from "react-loader-spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faSave, faUpload } from "@fortawesome/free-solid-svg-icons";
import { Helmet } from "react-helmet-async";
import dayjs from "dayjs";
import { Pairs } from "../../api/Pairs";
import { AggregationColor } from "antd/es/color-picker/color";

export default function TournamentEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { showToast } = useToasts();

  const [tournamentForm] = Form.useForm();
  const [pairsForm] = Form.useForm();

  const [editLoading, setEditLoading] = useState(false);
  const [editPairsLoading, setEditPairsLoading] = useState(false);
  const [tournamentImageUrl, setTournamentImageUrl] = useState<string>();
  const [fileList, setFileList] = useState<UploadProps["fileList"]>();
  const [usersList, setUsersList] = useState<Option[]>([]);
  const [tournament, setTournament] = useState<Tournament>();
  const [pairs, setPairs] = useState<Pair[]>();
  const [structureId, setStructureId] = useState<string>();
  const [error, setError] = useState(false);
  const [footballTeams, setFootballTeams] = useState<FootballTeam[]>();
  const [favorites, setFavorites] = useState<UserFavorites[]>([]);

  const fitp = Form.useWatch("fitp", tournamentForm);
  const date = Form.useWatch("date", tournamentForm);
  const tournamentPairs = Form.useWatch("pairs", pairsForm);
  const maxPairs = Form.useWatch("maxPairs", tournamentForm);
  const selectedSportId = Form.useWatch("sportId", tournamentForm);
  const selectedFormat = Form.useWatch("format", tournamentForm);

  const [sportOptions, setSportOptions] = useState<Option[]>([]);
  const [sportNames, setSportNames] = useState<Record<number, string>>({});
  // Sport a squadre (calcio e basket): iscrizione con squadre e rose.
  const isFootball =
    sportNames[selectedSportId] === "Football" ||
    sportNames[selectedSportId] === "Basketball";
  // Americano: solo padel/tennis, iscrizione individuale.
  const isAmericano = !isFootball && selectedFormat === TournamentFormat.Americano;

  const cancel = useCallback(() => navigate("/tornei"), [navigate]);

  const initSports = useCallback(() => {
    Sports.getSports({}).then(
      ({ data }) => {
        const names: Record<number, string> = {};
        const options = data.list.map((sport) => {
          names[sport.id] = sport.name;
          return { label: getSportName(sport.name), value: sport.id };
        });
        setSportOptions(options);
        setSportNames(names);
      },
      () => setSportOptions([])
    );
  }, []);

  const initFavorites = useCallback(() => {
    Structures.getHostFavorites().then(
      ({ data }) => {
        setFavorites(data.list);
        setUsersList(
          data.list.map((user) => ({
            label: `${user.user.name} ${user.user.familyName}`,
            value: user.userId,
          }))
        );
      },
      () => {
        showToast(
          "players-error",
          "Impossibile ottenere la lista dei giocatori",
          "",
          "danger"
        );
        setUsersList([]);
      }
    );
  }, [showToast]);

  const initStructure = useCallback(() => {
    Structures.getHostStructure().then(
      ({ data }) => {
        setStructureId(data.structure.id);
      },
      () => {
        setStructureId(undefined);
        Cookies.remove(window.__env.AUTH_TOKEN_NAME || "get-move-token");
        Cookies.remove(window.__env.AUTH_USER_NAME || "get-move-user");
        window.location.href = "/";
        showToast(
          "sports-error",
          "Errore nel reperimento della struttura",
          "",
          "danger"
        );
      }
    );
  }, [showToast]);

  const retrieveTournamentPairs = useCallback(() => {
    Pairs.getTournamentPairs(tournament!.id!).then(
      ({ data }) => {
        setPairs(data.list);
        pairsForm.setFieldsValue({ pairs: data.list });
      },
      () => {
        showToast(
          "pairs-error",
          "Errore nel reperimento delle coppie per questo torneo",
          "",
          "danger"
        );
        setPairs([]);
      }
    );
  }, [tournament, pairsForm, showToast]);

  const retrieveFootballTeams = useCallback(() => {
    AdminFootballTeams.getTeams(tournament!.id!).then(
      ({ data }) => setFootballTeams(data.teams),
      () => {
        showToast("teams-error", "Errore nel reperimento delle squadre", "", "danger");
        setFootballTeams([]);
      }
    );
  }, [tournament, showToast]);

  const retrieveTournament = useCallback(() => {
    Tournaments.getTournament(id!).then(
      ({ data }) => {
        if (data.tournament.imageUrl && data.tournament.imageUrl !== "") {
          Tournaments.getTournamentImage(data.tournament.id!).then(
            ({ data: imageData }) => {
              setTournamentImageUrl(imageData.url);
              setFileList([
                {
                  uid: data.tournament.id!,
                  name: imageData.url.split("/")[4].split("?")[0],
                  status: "done",
                  url: imageData.url,
                },
              ]);
            },
            () => {
              showToast(
                "tournament-image-error",
                "Errore nel reperimento del Logo del Torneo",
                "",
                "danger"
              );
            }
          );
        }
        tournamentForm.setFieldsValue({
          ...data.tournament,
          date: data.tournament.date ? dayjs(data.tournament.date) : null,
          endDate: data.tournament.endDate ? dayjs(data.tournament.endDate) : null,
          registrationDeadline: data.tournament.registrationDeadline
            ? dayjs(data.tournament.registrationDeadline)
            : null,
        });
        setTournament(data.tournament);
      },
      () => {
        showToast(
          "tournament-error",
          "Errore nel reperimento del Torneo",
          "",
          "danger"
        );
        setError(true);
      }
    );
  }, [id, showToast, tournamentForm]);

  useEffect(() => {
    if (id && tournament && !tournament.fitp) {
      if (isFootball) {
        retrieveFootballTeams();
      } else {
        retrieveTournamentPairs();
      }
    }
  }, [id, tournament, isFootball, retrieveTournamentPairs, retrieveFootballTeams]);

  useEffect(() => {
    initStructure();
    initFavorites();
    initSports();
    if (id) retrieveTournament();
    else {
      const newTournament = new Tournament();
      setTournament(newTournament);
      tournamentForm.setFieldsValue({ ...newTournament });
    }
  }, [retrieveTournament, id, tournamentForm, initStructure, initFavorites, initSports]);

  const blobUrlToFile = async (
    blobUrl: string,
    fileName: string,
    mimeType: string
  ) => {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new File([blob], fileName, { type: mimeType });
  };

  const handleEditTournamentForm = useCallback(
    async (values: any) => {
      setEditLoading(true);
      const fd = new FormData();
      Object.entries(values)
        .filter(([_, value]) => value !== null && value !== undefined)
        .forEach(([key, value]) => {
          if (["date", "endDate", "registrationDeadline"].includes(key)) {
            fd.append(key, (value as any).toISOString());
          } else {
            fd.append(key, value + "");
          }
        });
      if (fileList && fileList[0].url !== tournamentImageUrl) {
        const file = await blobUrlToFile(
          fileList[0]!.url!,
          fileList[0].name,
          getMimeType(fileList[0].name)
        );
        fd.append("image", file);
      }
      fd.append("structureId", structureId!);
      Tournaments.updateTournament(id!, fd).then(
        () => {
          setEditLoading(false);
          showToast(
            "update-tournament-success",
            "Torneo correttamente Modificato",
            "",
            "success"
          );
          navigate("/tornei");
        },
        () => {
          setEditLoading(false);
          showToast(
            "update-tournament-error",
            "Errore nella modifica del Torneo",
            "",
            "danger"
          );
        }
      );
    },
    [fileList, tournamentImageUrl, id, navigate, showToast, structureId]
  );

  const handleCreateTournamentSubmit = useCallback(
    async (values: any) => {
      setEditLoading(true);
      const fd = new FormData();
      Object.entries(values)
        .filter(([_, value]) => value !== null && value !== undefined)
        .forEach(([key, value]) => {
          if (["date", "endDate", "registrationDeadline"].includes(key)) {
            fd.append(key, (value as any).toISOString());
          } else {
            fd.append(key, value + "");
          }
        });
      if (fileList && fileList[0].url !== tournamentImageUrl) {
        const file = await blobUrlToFile(
          fileList[0]!.url!,
          fileList[0].name,
          getMimeType(fileList[0].name)
        );
        fd.append("image", file);
      }
      fd.append("structureId", structureId!);
      Tournaments.createTournament(fd).then(
        () => {
          setEditLoading(false);
          showToast(
            "create-tournament-success",
            "Torneo correttamente Creato",
            "",
            "success"
          );
          navigate("/tornei");
        },
        () => {
          setEditLoading(false);
          showToast(
            "create-tournament-error",
            "Errore nella creazione del Torneo",
            "",
            "danger"
          );
        }
      );
    },
    [structureId, fileList, tournamentImageUrl, navigate, showToast]
  );

  const handleTournamentSubmitForm = useCallback(
    (values: any) => {
      tournamentForm.validateFields();
      const hasErrors = tournamentForm
        .getFieldsError()
        .some((field) => field.errors.length > 0);
      if (!hasErrors) {
        if (id) {
          if (
            !fitp &&
            values.maxPairs &&
            pairs &&
            values.maxPairs < pairs.length
          ) {
            showToast(
              "update-tournament-error-size-error",
              "Per diminuire il numero massimo di coppie partecipanti, devi prima cancellare alcune coppie iscritte",
              "",
              "danger"
            );
          } else {
            handleEditTournamentForm(values);
          }
        } else handleCreateTournamentSubmit(values);
      }
    },
    [
      tournamentForm,
      handleCreateTournamentSubmit,
      handleEditTournamentForm,
      id,
      fitp,
      pairs,
      showToast,
    ]
  );

  const handlePairsSubmitForm = useCallback(
    async (values: any) => {
      const filteredPairs = values.pairs
        .filter((pair: Pair) => !!pair.player1Id || !!pair.player2Id)
        .map((pair: Pair) => ({
          player1Id: pair.player1Id,
          player2Id: pair.player2Id,
        }));
      setEditPairsLoading(true);
      Pairs.updateTournamentPairs(filteredPairs, id!, tournament!.type).then(
        () => {
          setEditPairsLoading(false);
          showToast(
            "update-pairs-success",
            "Hai correttamente aggiornato le coppie per questo torneo",
            "",
            "success"
          );
          navigate("/tornei");
        },
        () => {
          setEditPairsLoading(false);
          showToast(
            "update-pairs-error",
            "Errore nell'aggiornamento delle coppie",
            "",
            "danger"
          );
        }
      );
    },
    [navigate, showToast, id, tournament]
  );

  const handleChange: UploadProps["onChange"] = ({ file, fileList }) => {
    if (file.status === "removed") {
      setFileList([]);
    } else {
      const newFile: UploadFile = {
        uid: file.uid,
        name: file.name,
        status: "done",
        url: URL.createObjectURL(
          fileList[fileList.length - 1].originFileObj as Blob
        ),
      };
      setFileList([newFile]);
    }
  };

  const renderPairs = useCallback(() => {
    if (!tournament)
      return (
        <div className="d-flex justify-content-center py-5 my-5">
          <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
        </div>
      );
    return pairs ? (
      <Form form={pairsForm} onFinish={handlePairsSubmitForm}>
        <Card.Body>
          <Row className="mb-3 justify-content-center">
            <Form.List name="pairs">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => {
                    const player1Id = pairs[index]?.player1Id;
                    const player2Id = pairs[index]?.player2Id;

                    // Verifica se il player è presente nella usersList
                    const player1Exists = usersList.some(
                      (user) => user.value === player1Id
                    );
                    const player2Exists = usersList.some(
                      (user) => user.value === player2Id
                    );

                    return (
                      <Row key={key} className="justify-content-center">
                        <Col
                          xs={5}
                          key={key}
                          className="justify-content-center"
                        >
                          {/* Select Player 1 */}
                          <Form.Item
                            className="w-100"
                            {...restField}
                            name={[name, "player1Id"]}
                          >
                            <Select
                              showSearch
                              optionFilterProp="children"
                              placeholder="Seleziona Giocatore 1"
                              value={player1Exists ? player1Id : undefined}
                            >
                              {/* Opzione per nessun giocatore selezionato */}
                              <Select.Option value={undefined}>
                                Nessun giocatore
                              </Select.Option>
                              {/* Mostra il nome se il giocatore non esiste */}
                              {!player1Exists && player1Id && (
                                <Select.Option value={player1Id}>
                                  {`${pairs[index]?.player1?.name} ${pairs[index]?.player1?.familyName}` ||
                                    "Giocatore sconosciuto"}
                                </Select.Option>
                              )}
                              {usersList.map((user) => (
                                <Select.Option
                                  key={user.value}
                                  value={user.value}
                                  disabled={(tournamentPairs || []).some(
                                    (pair: any) =>
                                      (pair && pair.player1Id === user.value) ||
                                      (pair && pair.player2Id === user.value)
                                  )}
                                >
                                  {user.label}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={5} className="justify-content-center">
                          {/* Select Player 2 */}
                          <Form.Item
                            className="w-100"
                            {...restField}
                            name={[name, "player2Id"]}
                          >
                            <Select
                              showSearch
                              optionFilterProp="children"
                              placeholder="Seleziona Giocatore 2"
                              value={player2Exists ? player2Id : undefined}
                            >
                              {/* Opzione per nessun giocatore selezionato */}
                              <Select.Option value={undefined}>
                                Nessun giocatore
                              </Select.Option>
                              {/* Mostra il nome se il giocatore non esiste */}
                              {!player2Exists && player2Id && (
                                <Select.Option value={player2Id}>
                                  {`${pairs[index]?.player2?.name} ${pairs[index]?.player2?.familyName}` ||
                                    "Giocatore sconosciuto"}
                                </Select.Option>
                              )}
                              {usersList.map((user) => (
                                <Select.Option
                                  key={user.value}
                                  value={user.value}
                                  disabled={(tournamentPairs || []).some(
                                    (pair: any) =>
                                      (pair && pair.player1Id === user.value) ||
                                      (pair && pair.player2Id === user.value)
                                  )}
                                >
                                  {user.label}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={2}>
                          {/* Bottone per rimuovere la coppia */}
                          <Button
                            icon={<FontAwesomeIcon icon={faClose} />}
                            type="link"
                            danger
                            onClick={() => remove(name)}
                          />
                        </Col>
                      </Row>
                    );
                  })}

                  {(tournamentPairs || []).length < (maxPairs || 0) && (
                    <Button
                      type="dashed"
                      className="w-75"
                      onClick={() => add()}
                      block
                    >
                      Aggiungi Coppia
                    </Button>
                  )}
                </>
              )}
            </Form.List>
          </Row>
        </Card.Body>

        <div className="d-flex border-top p-2 px-3 justify-content-end">
          <RBBUtton
            disabled={editLoading || editPairsLoading}
            type="submit"
            variant="success"
            size="sm"
            className="border"
          >
            {editLoading || editPairsLoading ? (
              <TailSpin color="#FFF" width={15} height={15} />
            ) : (
              <>
                <FontAwesomeIcon className="me-2" icon={faSave} />
                <span>Salva</span>
              </>
            )}
          </RBBUtton>
        </div>
      </Form>
    ) : (
      <div className="d-flex justify-content-center py-5 my-5">
        {tournament!.fitp ? (
          <h6 className="text-warning">Torneo gestito dalla FITP</h6>
        ) : (
          <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
        )}
      </div>
    );
  }, [
    pairs,
    tournamentPairs,
    tournament,
    editLoading,
    editPairsLoading,
    handlePairsSubmitForm,
    maxPairs,
    pairsForm,
    usersList,
  ]);

  const renderFootballTeams = useCallback(() => {
    if (!footballTeams) return (
      <div className="d-flex justify-content-center py-5">
        <TailSpin height={40} width={40} color="#f25d0b" radius="1" />
      </div>
    );
    if (footballTeams.length === 0) return (
      <Card.Body style={{ padding: "16px 20px" }}>
        <Alert type="info" showIcon message="Nessuna squadra iscritta" />
      </Card.Body>
    );
    return (
      <Card.Body style={{ padding: "16px 20px" }}>
        <FootballRosterEditor
          teams={footballTeams}
          favorites={favorites}
          onChanged={retrieveFootballTeams}
        />
      </Card.Body>
    );
  }, [footballTeams, favorites, retrieveFootballTeams]);

  const sectionLabel = (text: string) => (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        color: "#888",
        marginBottom: 10,
        marginTop: 4,
        paddingBottom: 6,
        borderBottom: "1px solid #f0f0f0",
      }}
    >
      {text}
    </div>
  );
  
  const handleColorChange = useCallback((color: AggregationColor) => {
    tournamentForm.setFieldValue("mainColor", color.toHexString());
  }, [tournamentForm]);

  const renderTournamentForm = useCallback(() => {
    return (
      <Form form={tournamentForm} layout="vertical" onFinish={handleTournamentSubmitForm}>
        <Card.Body style={{ padding: "20px 24px" }}>

          {/* Sezione: Informazioni generali */}
          {sectionLabel("Informazioni generali")}
          <Row>
            <Col xs={12}>
              <Form.Item
                name="name"
                label="Nome torneo"
                rules={[{ required: true, message: "Il nome è obbligatorio" }]}
              >
                <Input placeholder="Es. Torneo Estivo 2025" />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="sportId"
                label="Sport"
                rules={[{ required: true, message: "Lo sport è obbligatorio" }]}
              >
                <Select
                  placeholder="Seleziona sport..."
                  options={sportOptions}
                  onSelect={(value: any) => tournamentForm.setFieldValue("sportId", value)}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="mainColor"
                label="Colore principale"
              >
                <ColorPicker defaultValue="#f25d0b" onChange={handleColorChange} showText allowClear />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="type"
                label="Tipologia"
                rules={[{ required: true, message: "La tipologia è obbligatoria" }]}
              >
                <Select
                  placeholder="Seleziona tipologia..."
                  options={[
                    { label: "Maschile", value: PairType.Men },
                    { label: "Femminile", value: PairType.Women },
                    { label: "Misto", value: PairType.Mixed },
                  ]}
                  onSelect={(value: any) => tournamentForm.setFieldValue("type", value)}
                />
              </Form.Item>
            </Col>
            {!isFootball && (
              <Col xs={12} md={6}>
                <Form.Item
                  name="format"
                  label="Formato"
                  tooltip="Standard: iscrizione a coppie. Americano: iscrizione individuale, compagni a rotazione."
                >
                  <Select
                    placeholder="Seleziona formato..."
                    options={[
                      { label: "Standard (a coppie)", value: TournamentFormat.Standard },
                      { label: "Americano (individuale)", value: TournamentFormat.Americano },
                    ]}
                    onSelect={(value: any) => tournamentForm.setFieldValue("format", value)}
                  />
                </Form.Item>
              </Col>
            )}
            <Col xs={12}>
              <Form.Item name="description" label="Descrizione">
                <Input.TextArea placeholder="Descrizione opzionale..." autoSize={{ minRows: 2 }} />
              </Form.Item>
            </Col>
          </Row>

          {/* Sezione: Date */}
          {sectionLabel("Date")}
          <Row>
            <Col xs={12} md={6}>
              <Form.Item
                name="date"
                label="Data di inizio"
                rules={[{ required: true, message: "La data è obbligatoria" }]}
              >
                <DatePicker className="w-100" format="DD/MM/YYYY" placeholder="Seleziona data..." />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="endDate"
                label="Data di fine (opzionale)"
                tooltip="Se non indicata, coincide con la data di inizio"
              >
                <DatePicker className="w-100" format="DD/MM/YYYY" placeholder="Seleziona data fine..." minDate={date} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="registrationDeadline"
                label="Scadenza iscrizioni"
                rules={[{ required: true, message: "La scadenza è obbligatoria" }]}
              >
                <DatePicker className="w-100" format="DD/MM/YYYY" placeholder="Seleziona scadenza..." maxDate={date} />
              </Form.Item>
            </Col>
          </Row>

          {/* Sezione: Impostazioni */}
          {sectionLabel("Impostazioni")}
          <Row>
            <Col xs={12}>
              <Form.Item name="fitp" valuePropName="checked" label="Torneo ufficiale FITP">
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="publicBracket"
                valuePropName="checked"
                label="Tabellone pubblico"
                tooltip="Se attivo, il tabellone è visibile nell'app a tutti gli utenti, anche a chi non è iscritto al torneo."
              >
                <Switch />
              </Form.Item>
            </Col>
            {fitp && (
              <Col xs={12}>
                <Form.Item
                  name="link"
                  label="Link al portale FITP"
                  rules={[{ required: true, message: "Il link è obbligatorio" }]}
                >
                  <Input placeholder="https://..." />
                </Form.Item>
              </Col>
            )}
            {!fitp && !isAmericano && (
              <Col xs={12} md={6}>
                <Form.Item
                  name="maxPairs"
                  label={isFootball ? "Max squadre" : "Max coppie"}
                  rules={[{ required: true, message: "Il campo è obbligatorio" }]}
                >
                  <InputNumber
                    className="w-100"
                    placeholder={isFootball ? "Numero squadre..." : "Numero coppie..."}
                    min={1}
                    max={100}
                  />
                </Form.Item>
              </Col>
            )}
            {!fitp && isAmericano && (
              <>
                <Col xs={12} md={6}>
                  <Form.Item
                    name="maxPlayers"
                    label="Max giocatori"
                    rules={[{ required: true, message: "Il campo è obbligatorio" }]}
                  >
                    <InputNumber
                      className="w-100"
                      placeholder="Numero giocatori..."
                      min={4}
                      max={100}
                    />
                  </Form.Item>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Item
                    name="americanoTargetPoints"
                    label="Punti a partita"
                    tooltip="Punteggio obiettivo di ogni partita (default 32)."
                  >
                    <InputNumber className="w-100" placeholder="32" min={1} max={200} />
                  </Form.Item>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Item
                    name="americanoWinBy"
                    label="Scarto minimo"
                    tooltip="Scarto minimo di punti per vincere (default 2)."
                  >
                    <InputNumber className="w-100" placeholder="2" min={1} max={10} />
                  </Form.Item>
                </Col>
              </>
            )}
            {isFootball && (
              <Col xs={12} md={6}>
                <Form.Item name="maxPlayersPerTeam" label="Max giocatori per squadra">
                  <InputNumber className="w-100" placeholder="Es. 11" min={1} max={30} />
                </Form.Item>
              </Col>
            )}
            <Col xs={12}>
              <Form.Item label="Locandina">
                <Upload
                  fileList={fileList}
                  listType="picture"
                  showUploadList={{ showDownloadIcon: true }}
                  beforeUpload={() => false}
                  onChange={handleChange}
                >
                  <Button icon={<FontAwesomeIcon icon={faUpload} className="me-2" />}>
                    {!fileList || fileList.length === 0 ? "Carica immagine" : "Cambia immagine"}
                  </Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Card.Body>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 24px",
            borderTop: "1px solid #f0f0f0",
            background: "#fafafa",
          }}
        >
          <RBBUtton disabled={editLoading} type="button" variant="light" size="sm" className="border" onClick={cancel}>
            Annulla
          </RBBUtton>
          <RBBUtton disabled={editLoading} type="submit" variant="success" size="sm">
            {editLoading ? (
              <TailSpin color="#FFF" width={14} height={14} />
            ) : (
              <>
                <FontAwesomeIcon className="me-2" icon={faSave} />
                Salva
              </>
            )}
          </RBBUtton>
        </div>
      </Form>
    );
  }, [
    tournamentForm,
    fileList,
    editLoading,
    cancel,
    handleTournamentSubmitForm,
    fitp,
    date,
    sportOptions,
    isFootball,
    isAmericano,
    handleColorChange
  ]);

  const renderAsyncContent = useCallback(() => {
    if (error)
      return (
        <Alert
          type="error"
          showIcon
          message="Si è verificato un errore nel reperimento dei dati sul Campo. Riprova più tardi."
        />
      );

    return tournament && structureId ? (
      renderTournamentForm()
    ) : (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
      </div>
    );
  }, [error, tournament, renderTournamentForm, structureId]);

  const cardStyle: React.CSSProperties = {
    border: "1px solid #e8e8e8",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  };

  const cardHeaderStyle: React.CSSProperties = {
    padding: "12px 20px",
    borderBottom: "1px solid #f0f0f0",
    background: "#fafafa",
  };

  return (
    <Container className="my-3">
      <Helmet>
        <title>{id ? "Modifica" : "Nuovo"} Torneo</title>
      </Helmet>

      <Breadcrumb className="mb-3">
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/tornei" }}>
          Tornei
        </Breadcrumb.Item>
        {id && (
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/tornei/${id}` }}>
            {tournament ? tournament.name : <TailSpin color="#f25d0b" width={14} height={14} />}
          </Breadcrumb.Item>
        )}
        <Breadcrumb.Item active>{id ? "Modifica" : "Nuovo Torneo"}</Breadcrumb.Item>
      </Breadcrumb>

      {id ? (
        <Row className="g-3">
          <Col xs={12} md={6}>
            <Card style={cardStyle}>
              <div style={cardHeaderStyle}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>Modifica Torneo</span>
                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>Modifica i valori esistenti e salva</div>
              </div>
              {renderAsyncContent()}
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card style={cardStyle}>
              <div style={cardHeaderStyle}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{isFootball ? "Squadre Iscritte" : "Coppie Iscritte"}</span>
              </div>
              {isFootball ? renderFootballTeams() : renderPairs()}
            </Card>
          </Col>
        </Row>
      ) : (
        <Row className="justify-content-center">
          <Col xs={12} md={8} lg={6}>
            <Card style={cardStyle}>
              <div style={cardHeaderStyle}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>Nuovo Torneo</span>
                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>Compila i campi per creare un nuovo torneo</div>
              </div>
              {renderAsyncContent()}
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
}
